import { useEffect, useState, useMemo } from "react";
import React from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useEval } from "../hooks/fetch_api_hook/useEval";
import EvalDetail from "../components/VueEvaluation/EvalDetail";
import { isInPeriode } from "../utils/periode";
import useConfirm from "../hooks/ui/useConfirm";
import { useDeleteEval } from "../hooks/delete_api_hook/useDeleteEval";
import { useClasseContext } from "../context/ClasseContext";
import Loading from "../components/general/Loading";
import axios from "axios";
import { url } from "../utils/confserveur";
import { useUser } from "../context/UserContext";
import { formatLettresDate } from "../utils/ClasseEvaluations";
import { Eye, Edit, Trash2, CheckCircle, Clock, FileText, Sparkles, Target, AlertTriangle, User, ChevronDown } from 'lucide-react';
import EditEval from "../components/FormEvaluation/EditEval";
import { filtrerGroupesDeBesoin } from "../utils/groupesDeBesoin";

function Evaluations({commune}) {
  const {idclasse} = useParams();
  const {classeNom, groupNom, ActivePeriode} = useClasseContext();
  const { etablissementData, user } = useUser();
  
  const [selectedEvalId, setSelectedEvalId] = useState(null);
  const [selectedEval, setSelectedEval] = useState({});
  const [editEvalId, setEditEvalId] = useState(null);

  // State pour l'affectation des profs aux classes manquantes
  const [selectedProfs, setSelectedProfs] = useState({}); // { "titre-classeNom": profId }
  const [isAffecting, setIsAffecting] = useState(false);

  // State pour l'expansion des lignes de devoirs communs
  const [expandedEval, setExpandedEval] = useState(null); // titre du devoir commun expanded

  // Handler pour sélectionner un prof dans le dropdown
  const handleProfSelect = (devoirTitre, classeNom, profId) => {
    const key = `${devoirTitre}-${classeNom}`;
    setSelectedProfs(prev => ({ ...prev, [key]: profId }));
  };

  // Handler pour affecter un prof à une classe (assigner le devoir commun)
  const handleAffecterProf = async (devoir, classe) => {
    const key = `${devoir.titre}-${classe.classeNom}`;
    const profId = selectedProfs[key];

    if (!profId) {
      alert("Veuillez sélectionner un professeur.");
      return;
    }

    const profSelected = classe.profs.find(p => p.id === parseInt(profId));
    if (!profSelected) return;

    setIsAffecting(true);
    try {
      // Payload adapté selon la cible : pour 'classes' on envoie classe_id,
      // pour 'groupes' on envoie groupe_id (le champ classe.classeId contient
      // l'id de l'entité — classe ou groupe — selon le scope du devoir).
      const cible = devoir.cible || 'classes';
      const assignData = {
        titre: devoir.titre,
        cible,
        classe_id: cible === 'classes' ? classe.classeId : null,
        groupe_id: cible === 'groupes' ? classe.classeId : null,
        createur_id: profSelected.id
      };
      await axios.post(`${url}evaluations/assign-devoir-commun/`, assignData);

      // Retirer la sélection
      setSelectedProfs(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      alert(`Devoir commun assigné pour ${classe.classeNom} avec ${profSelected.prenom} ${profSelected.nom} comme responsable.`);

      // Recharger la page pour rafraîchir les données
      window.location.reload();
    } catch (error) {
      console.error("Erreur lors de l'assignation du devoir commun:", error);
      alert("Erreur lors de l'assignation. Veuillez réessayer.");
    } finally {
      setIsAffecting(false);
    }
  };
  
  // Toute la logique existante SANS CHANGEMENT
  const base = `by-etablissement/?etablissement_id=${etablissementData.id}`;
  const query = commune ? `${base}&commune=true` : base;
  const { data: evals, isLoading: isLoadingEval, isError } = useEval(query, selectedEvalId);

  const [listEval, setListEvals] = useState([]);
  const [filteredEvals, setFilteredEvals] = useState([]);
  const { deleteEvaluation, isLoading: isLoadingDeleteEval } = useDeleteEval();
  const [filters, setFilters] = useState({
    status: 'Tous',
    search: '',
    date: ''
  });

  // Infos complètes pour chaque devoir commun (entités affectées + manquantes).
  // Supporte les deux scopes : cible 'classes' (entité = classe entière) ou
  // cible 'groupes' (entité = groupe de besoins).
  // L'API du résultat reste identique (classesAffectees, classesManquantes, nbManquantes)
  // pour ne pas casser le rendu existant : le champ `classeNom` contient le nom de
  // l'entité (classe ou groupe selon cible), `classeId` son id.
  const devoirsCommunsInfo = useMemo(() => {
    if (!commune || !evals || !etablissementData?.classes) return {};

    const matiereProf = user?.matiere?.toLowerCase();
    if (!matiereProf) return {};

    // 1. Grouper par titre + détecter le scope
    const devoirsCommuns = {};
    evals.forEach(ev => {
      const cible = ev.cible || 'classes'; // fallback rétro-compat
      const entiteNom = cible === 'groupes' ? ev.groupe_nom : ev.classe_nom;
      const niveau = ev.niveau || entiteNom?.[0]; // "6A" → "6"
      if (!niveau || !entiteNom) return;

      if (!devoirsCommuns[ev.titre]) {
        devoirsCommuns[ev.titre] = { titre: ev.titre, niveau, cible, affectees: [] };
      }

      const profResponsable = etablissementData.professeurs.find(p => p.id === ev.createur);
      devoirsCommuns[ev.titre].affectees.push({
        classeNom: entiteNom,
        classeId: cible === 'groupes' ? ev.groupe : ev.classe,
        profNom: profResponsable ? `${profResponsable.prenom} ${profResponsable.nom}` : 'Inconnu'
      });
    });

    // 2. Calcul des entités manquantes selon le scope
    const result = {};
    Object.values(devoirsCommuns).forEach(devoir => {
      const affecteesIds = new Set(
        devoir.affectees.map(a => devoir.cible === 'groupes' ? a.classeId : a.classeNom)
      );

      let manquantes = [];

      if (devoir.cible === 'groupes') {
        // Groupes de besoin du niveau dans la matière, pas encore affectés (un
        // demi-groupe n'est jamais une cible de devoir commun : pas « manquant »)
        const groupesDuNiveau = filtrerGroupesDeBesoin(etablissementData.groupes).filter(g => {
          const matchNiveau = g.niveau === `${devoir.niveau}eme` || g.niveau === devoir.niveau;
          const matchMatiere = g.matiere?.toLowerCase() === matiereProf;
          return matchNiveau && matchMatiere;
        });

        manquantes = groupesDuNiveau
          .filter(g => !affecteesIds.has(g.id))
          .map(groupe => {
            const profResponsable = etablissementData.professeurs.find(p => p.id === groupe.professeur);
            // 1 prof unique par groupe (pas de choix réel, mais on respecte l'API du dropdown)
            const profs = profResponsable ? [{
              id: profResponsable.id,
              nom: profResponsable.nom,
              prenom: profResponsable.prenom,
              coordonateur: profResponsable.coordonateur
            }] : [];
            return {
              classeId: groupe.id,
              classeNom: groupe.nom,
              profs
            };
          });
      } else {
        // Cible 'classes' — comportement historique
        const classesDuNiveau = etablissementData.classes
          .filter(c => c.nom.startsWith(devoir.niveau) && Number(c.nombre_eleves) > 0);

        manquantes = classesDuNiveau
          .filter(c => !affecteesIds.has(c.nom))
          .map(classe => {
            const profsDisponibles = etablissementData.professeurs
              .filter(p =>
                p.matiere?.toLowerCase() === matiereProf &&
                p.classes_nom?.includes(classe.nom)
              )
              .map(p => ({
                id: p.id,
                visitorId: p.user,
                nom: p.nom,
                prenom: p.prenom,
                coordonateur: p.coordonateur
              }));
            return {
              classeId: classe.id,
              classeNom: classe.nom,
              profs: profsDisponibles
            };
          });
      }

      result[devoir.titre] = {
        titre: devoir.titre,
        niveau: devoir.niveau,
        cible: devoir.cible,
        classesAffectees: devoir.affectees,
        classesManquantes: manquantes,
        nbManquantes: manquantes.length
      };
    });

    return result;
  }, [commune, evals, etablissementData, user]);

  // Toutes les fonctions existantes SANS CHANGEMENT
  const isValidDateFormat = (dateString) => {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateString)) return false;
    
    const [day, month, year] = dateString.split('/').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
  };

  // Tous les useEffect SANS CHANGEMENT
  useEffect(() => {
    if (!commune && evals && ActivePeriode && ActivePeriode.date_debut && ActivePeriode.date_fin) {
      let filteredEvals = evals.filter(ev => {
        const isCreator = ev.createur === user.id;  // user.id doit être l'ID du prof connecté
        return isInPeriode(ev.date, ActivePeriode) && isCreator;
      });

      if (classeNom === '') {
        filteredEvals = filteredEvals.filter(ev => ev.groupe_nom === groupNom);
      } else {
        filteredEvals = filteredEvals.filter(ev => ev.classe_nom === classeNom);
      }
      
      setListEvals(filteredEvals);
    } else {
      const filteredEvals = evals.filter(evaluation => evaluation.titre.includes('Commun') && evaluation.etablissement_id === etablissementData.id);

      const groupedEvals = filteredEvals.reduce((acc, current) => {
        const mainTitle = current.titre.includes('Commun') && current.titre;
        const key = `${mainTitle}-${current.date}`;
        
        if (!acc[mainTitle]) {
          acc[mainTitle] = {
            id: current.id,
            titre: mainTitle,
            date: current.date,
            classe: null,
            classe_nom: current.classe_nom,
            groupe_nom: current.groupe_nom,
            cible: current.cible || 'classes',
            savoir_faires: current.savoir_faires,
            statut: current.statut,
          };
        }
        return acc;
      }, {});

      const result = Object.values(groupedEvals);
      setListEvals(result);
    }
  }, [evals, commune, etablissementData.id, ActivePeriode, classeNom, groupNom, user.id]);


  useEffect(() => {
    const applyFilters = (evalsList) => {
      let filtered = [...evalsList];

      if (filters.status !== 'Tous') {
        filtered = filtered.filter(ev => {
          if (filters.status === 'Terminées') return ev.statut === 'terminee';
          if (filters.status === 'En cours') return ev.statut === 'en_attente';
          return true;
        });
      }

      if (filters.search.trim()) {
        filtered = filtered.filter(ev => 
          ev.titre.toLowerCase().includes(filters.search.toLowerCase().trim())
        );
      }

      if (filters.date.trim()) {
        if (isValidDateFormat(filters.date)) {
          const [day, month, year] = filters.date.split('/');
          const searchDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          
          filtered = filtered.filter(ev => {
            return ev.date === searchDate;
          });
        }
      }

      return filtered;
    };
    
    const filtered = applyFilters(listEval);
    setFilteredEvals(filtered);
  }, [listEval, filters]);

  // Toutes les fonctions helper SANS CHANGEMENT
  const getMoyenneEvals = (listeval) => {
    const moyennes = listeval
      .filter(ev => ev.moyenne_scores != null)
      .map(ev => ev.moyenne_scores);
    
    return moyennes.length > 0 
      ? moyennes.reduce((a, b) => a + b, 0) / moyennes.length 
      : 0;
  };

  const handleClick = (item) => {
    if(commune) return true;
    setSelectedEvalId(item.id);
    const Eval = {
      titre: item.titre,
      date: item.date,
      statut: item.statut,
      nombre_eleves: item.nombre_eleves,
      moyenne_scores: item.moyenne_scores,
      savoir_faires: item.savoir_faires,
      eleves: item.eleves
    };
    setSelectedEval(Eval);
  };

  const checkClassOrGroup = (item) => {
    const id = parseInt(idclasse, 10);
    if(commune) return true;
    if (classeNom === "" && groupNom !== "") {
      return item.groupe === id;
    } else {
      return item.classe === id;
    }
  };

  const { confirm, ConfirmDialog } = useConfirm();

  const handleDelete = async (event, evaluation) => {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = await confirm({ title: "Supprimer cette évaluation ?", confirmLabel: "Supprimer", variant: "danger" });
    if (confirmed) {
      if(!commune) {
        await deleteEvaluation(evaluation.id);
      } else {
        const hasSavoirFairesNonZero = evals
          .filter((evallist) => evallist.titre === evaluation.titre)
          .some((evallist) => {
            const { nb_orange, nb_rouge, nb_vert, nb_vert_plus, nb_vert_pm, nb_yellow } = evallist.savoir_faires;
            return (
              nb_orange !== 0 || nb_rouge !== 0 || nb_vert !== 0 ||
              nb_vert_plus !== 0 || nb_vert_pm !== 0 || nb_yellow !== 0
            );
          });

        if(!hasSavoirFairesNonZero) {
          try {
            const response = await axios.delete(`${url}evaluations/delete-by-criteria/`, {
              params: {
                titre: evaluation.titre,
                niveau: (evaluation.classe_nom ?? evaluation.groupe_nom)?.[0],
                etablissement_id: etablissementData.id,
              },
            });
          } catch (error) {
            console.error("Erreur lors de la suppression des évaluations :", error.response?.data || error.message);
          }
        } else {
          alert("Certains devoirs communs contiennent encore des résultats. Les professeurs doivent les supprimer avant de pouvoir supprimer le devoir commun.");
          return;
        }
      }

      setListEvals(prevListEval => prevListEval.filter(e => e.id !== evaluation.id));
      setSelectedEvalId(null);
    }
  };

  const getNbTerminée = (evals) => {
    return evals.reduce((acc, evaluation) => {
      return evaluation.statut === 'terminee' ? acc + 1 : acc;
    }, 0);
  };

  const getNbEnCours = (evals) => {
    return evals.reduce((acc, evaluation) => {
      return evaluation.statut === 'en_attente' ? acc + 1 : acc;
    }, 0);
  };

  // Conditions de chargement SANS CHANGEMENT
  if(isLoadingEval) return <Loading loadingText={"Chargement des évaluations ..."} />;
  if(isError) return <div>Erreur lors du chargement des évaluations !</div>;
  if(isLoadingDeleteEval) return <Loading loadingText={"Suppression en cours..."} />;
  if(user.role !== "professeur") {
    return (
      <div className="flex flex-row justify-between bg-white p-4 rounded-lg shadow-md text-left text-sm xl:text-xs 2xl:text-sm 3xl:text-lg h-fit mb-5">
        Désolé ! Cette espace est réservé au professeur de la classe concernée.
      </div>
    );
  }

  return (
    <div className="w-full px-5 text-black">
      {ConfirmDialog}
      <div className="bg-white/95 mt-5 backdrop-blur-sm rounded-2xl shadow-xl pb-3  sm:pb-4 lg:pb-6 ">
        {!commune ? (
          /* En-tête pour évaluations normales - avec motifs décoratifs */
          <div className="relative rounded-t-2xl overflow-hidden mb-6">
            {/* Gradient de fond animé */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-90 transition-all duration-1000"></div>

            {/* Motifs décoratifs */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 -translate-y-48"></div>
              <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-white rounded-full translate-y-40"></div>
            </div>

            {/* Contenu */}
            <div className="relative z-1 p-4 sm:p-6 text-white">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                  <h1 className="text-lg sm:text-xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-semibold mb-2">Évaluations</h1>
                  <p className="text-sm sm:text-base xl:text-sm 2xl:text-base 3xl:text-xl text-white/90">
                    <span className="hidden sm:inline">Listes d'évaluations de la classe de</span>
                    <span className="sm:hidden">Classe :</span> {classeNom ? classeNom : groupNom}
                  </p>
                </div>
                <Link to={`/home/evals/${idclasse}/add`}>
                  <button className="w-full sm:w-auto bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all text-sm sm:text-base lg:text-lg duration-200 hover:-translate-y-0.5">
                    <span className="hidden sm:inline">+ AJOUTER UNE ÉVALUATION</span>
                    <span className="sm:hidden">+ AJOUTER</span>
                  </button>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          /* Section devoirs communs - avec design similaire à ProgressionNiveau */
          <div className="relative rounded-t-2xl overflow-hidden mb-6">
            {/* Gradient de fond */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-600 opacity-90"></div>

            {/* Motifs décoratifs */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 -translate-y-48"></div>
              <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-white rounded-full translate-y-40"></div>
            </div>

            {/* Contenu */}
            <div className="relative z-1 px-4 sm:px-6 py-6 sm:py-8">
              {/* Titre et description */}
              <div className="mb-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur-md rounded-xl">
                    <FileText className="w-6 h-6 sm:w-8 sm:h-8 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-8 3xl:h-8 text-white" />
                  </div>
                  <h1 className="text-xl sm:text-2xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
                    Devoirs Communs
                    <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-8 3xl:h-8 text-yellow-300 animate-pulse" />
                  </h1>
                </div>

                <p className="text-base sm:text-lg xl:text-base 2xl:text-lg 3xl:text-xl text-white/90 font-light ml-12 sm:ml-16">
                  Gérez les évaluations communes à toutes les classes d'un niveau
                </p>
              </div>

              {/* Zone d'actions */}
              <div className="bg-white/95 backdrop-blur-lg rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 border border-white/50">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 sm:gap-6">
                  {/* Message d'accueil */}
                  <div className="flex-1">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 sm:w-5 sm:h-5 xl:w-4 xl:h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                      Bienvenue sur votre espace de devoirs communs
                    </h2>

                    <p className="text-sm sm:text-base xl:text-sm 2xl:text-base 3xl:text-lg text-gray-600">
                      Ajoutez un devoir commun pour un niveau. Il sera automatiquement créé pour toutes les classes concernées.
                    </p>
                  </div>

                  {/* Bouton Ajouter */}
                  <Link to={`/home/add-common-eval`}>
                    <button className="w-full lg:w-auto group relative overflow-hidden bg-gradient-to-r from-blue-500 to-sky-600
                               text-white font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base xl:text-sm 2xl:text-base 3xl:text-lg
                               shadow-lg hover:shadow-xl transform hover:scale-105
                               transition-all duration-300 flex items-center justify-center gap-2">
                      <span className="hidden sm:inline">+ Ajouter un devoir commun</span>
                      <span className="sm:hidden">+ Nouveau devoir</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Statistiques - responsive */}
        {!selectedEvalId && (
          <div className="grid px-4 grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
              <div className="text-xl sm:text-2xl lg:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-blue-500 mb-1 sm:mb-2">{listEval.length}</div>
              <div className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide">
                <span className="hidden sm:inline">Évaluations créées</span>
                <span className="sm:hidden">Total</span>
              </div>
            </div>
            
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
              <div className="text-xl sm:text-2xl lg:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-green-500 mb-1 sm:mb-2">{getNbTerminée(listEval)}</div>
              <div className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide">Terminées</div>
            </div>
            
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-yellow-500"></div>
              <div className="text-xl sm:text-2xl lg:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-yellow-500 mb-1 sm:mb-2">{getNbEnCours(listEval)}</div>
              <div className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide">En cours</div>
            </div>
            
            {!commune && (
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden col-span-2 lg:col-span-1">
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                <div className="text-xl sm:text-2xl lg:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-indigo-500 mb-1 sm:mb-2">{parseFloat(getMoyenneEvals(listEval).toFixed(2))}</div>
                <div className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide">
                  <span className="hidden sm:inline">Moyenne des évaluations</span>
                  <span className="sm:hidden">Moyenne</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Section liste - responsive */}
        {!selectedEvalId && (
          <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100">
            <h2 className="text-base sm:text-lg lg:text-xl xl:text-base 2xl:text-lg 3xl:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Liste des évaluations</h2>
            
            {/* Filtres - responsive */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option>Tous</option>
                <option>En cours</option>
                <option>Terminées</option>
              </select>
              
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 sm:flex-initial"
              />
              
              <input
                type="text"
                placeholder="jj/mm/aaaa"
                value={filters.date}
                onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {filters.date && !isValidDateFormat(filters.date) && (
                <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-3 p-2 sm:p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-amber-200 rounded-lg sm:rounded-xl">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs sm:text-sm font-bold">!</span>
                  </div>
                  <span className="text-amber-700 text-xs font-semibold">
                    Format requis : jj/mm/yyyy
                  </span>
                </div>
              )}
            </div>

            {/* Version Desktop : Table */}
            <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-500 to-sky-600 text-white">
                    <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">État</th>
                    <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Nom</th>
                    <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Date</th>
                    {!commune && <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Absents</th>}
                    {!commune ?
                      <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Moyenne</th> :
                      <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Niveau</th>
                    }
                    {!commune && <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Compétences</th>}
                    {commune && <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Affectation</th>}
                    <th className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-left text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvals.map((item) => {
                    if(checkClassOrGroup(item)) {
                      const totalSavoirFaires =
                        item.savoir_faires.nb_vert_plus + item.savoir_faires.nb_vert +
                        item.savoir_faires.nb_vert_pm + item.savoir_faires.nb_yellow +
                        item.savoir_faires.nb_orange + item.savoir_faires.nb_rouge +
                        item.savoir_faires.nb_no_scores;
                      const divisor = totalSavoirFaires || 1;

                      // Infos du devoir commun pour cette évaluation
                      const devoirInfo = commune ? devoirsCommunsInfo[item.titre] : null;
                      const nbManquantes = devoirInfo?.nbManquantes || 0;
                      const isExpanded = expandedEval === item.titre;

                      return (
                        <React.Fragment key={item.id}>
                          <tr
                            onClick={() => commune ? setExpandedEval(isExpanded ? null : item.titre) : handleClick(item)}
                            className={`text-xs xl:text-xs 2xl:text-sm 3xl:text-base border-b border-gray-100 hover:bg-blue-50 cursor-pointer hover:backdrop-blur-sm transition-all duration-200 hover:scale-[1.001] ${isExpanded ? 'bg-blue-50' : ''}`}
                          >
                            <td className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4">
                              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                                item.statut === 'terminee'
                                  ? 'bg-green-100 text-green-800 border border-green-200'
                                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                              }`}>
                                {item.statut === 'terminee' ? <CheckCircle size={14} /> : <Clock size={14} />}
                                {item.statut}
                              </span>
                            </td>
                            <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4">
                              <span className={`font-medium ${commune ? 'text-black' : 'text-blue-600 hover:text-blue-800 hover:underline'} transition-colors duration-200`}>
                                {item.titre}
                              </span>
                            </td>
                            <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4">{formatLettresDate(item.date)}</td>
                            {!commune ? (
                              <>
                                <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4 text-gray-700">{item.nombre_eleves}</td>
                                <td className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4 text-gray-700 font-medium">
                                  {item.moyenne_scores ? parseFloat(item.moyenne_scores.toFixed(2)) : "Pas de note"}
                                </td>
                                <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4">
                                  <div className="flex items-center h-5 bg-white rounded border border-gray-200 overflow-hidden shadow-sm">
                                    <div className="bg-green-800 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert_plus / divisor})` }}></div>
                                    <div className="bg-green-500 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert / divisor})` }}></div>
                                    <div className="bg-teal-200 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert_pm / divisor})` }}></div>
                                    <div className="bg-yellow-500 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_yellow / divisor})` }}></div>
                                    <div className="bg-orange-600 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_orange / divisor})` }}></div>
                                    <div className="bg-red-600 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_rouge / divisor})` }}></div>
                                    <div className="bg-zinc-100 border-2 h-6" style={{ width: `calc(100% * ${item.savoir_faires.nb_no_scores / divisor})` }}></div>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4">
                                  <div className="flex flex-col gap-1.5">
                                    <span>{(devoirInfo?.niveau || item.classe_nom?.[0]) ? `${devoirInfo?.niveau || item.classe_nom[0]}ème` : '—'}</span>
                                    {devoirInfo?.cible && (
                                      <span className={`inline-flex items-center self-start px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                                        devoirInfo.cible === 'groupes'
                                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                                      }`}>
                                        {devoirInfo.cible === 'groupes' ? 'Affecté aux groupes' : 'Affecté aux classes'}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-4 px-4 xl:py-2 xl:px-2 2xl:py-3 2xl:px-3 3xl:py-4 3xl:px-4">
                                  <div className="flex items-center gap-2">
                                    {nbManquantes > 0 ? (
                                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 cursor-pointer animate-pulse">
                                        <AlertTriangle size={14} className="animate-bounce" />
                                        {nbManquantes} à affecter
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold rounded-full border border-green-200 cursor-pointer hover:bg-green-200 transition-all">
                                        <CheckCircle size={14} />
                                        Complet
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </>
                            )}
                            <td className="px-4 py-4 xl:px-2 xl:py-2 2xl:px-3 2xl:py-3 3xl:px-4 3xl:py-4">
                              <div className="flex gap-2 items-center">
                                {!commune && (
                                  <button className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-all duration-200 hover:-translate-y-0.5" title="Consulter">
                                    <Eye size={16} />
                                  </button>
                                )}
                                {!commune && !/devoir commun/i.test(item.titre) && (
                                      <button onClick={(e) => { e.stopPropagation(); setEditEvalId(item.id); }} className="p-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-all duration-200 hover:-translate-y-0.5" title="Modifier">
                                      <Edit size={16} />
                                    </button>
                                )}
                                {(commune || !/devoir commun/i.test(item.titre)) && (
                                  <button className="p-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-all duration-200 hover:-translate-y-0.5" title="Supprimer"
                                    onClick={(e) => handleDelete(e, item)}>
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Section d'expansion pour les devoirs communs */}
                          {commune && isExpanded && devoirInfo && (
                            <tr>
                              <td colSpan="6" className="px-4 py-4 bg-gray-50 border-b border-gray-200">
                                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                                  <h2 className="font-semibold text-gray-800 mb-5 flex items-center gap-2 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
                                    <User size={18} className="text-emerald-600" />
                                    Affectation des responsables de saisie
                                  </h2>

                                  <div className="space-y-4">
                                    {/* Section : Classes affectées */}
                                    {devoirInfo.classesAffectees.length > 0 && (
                                      <div className="border border-green-200 rounded-lg overflow-hidden">
                                        <div className="bg-green-50 px-4 py-2 border-b border-green-200 flex items-center gap-2">
                                          <CheckCircle size={14} className="text-green-600" />
                                          <span className="font-medium text-green-800 text-sm">
                                            Affectées ({devoirInfo.classesAffectees.length})
                                          </span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                          {devoirInfo.classesAffectees.map((classe, idx) => (
                                            <div key={idx} className=" xl:text-xs 2xl:text-sm 3xl:text-xl text-base
                                          grid grid-cols-3 items-center px-4 py-3 hover:bg-gray-50">
                                              <span className="font-medium text-gray-800">{classe.classeNom}</span>
                                              <span className="text-gray-600 flex items-center gap-2">
                                                <User size={14} className="text-gray-400" />
                                                {classe.profNom}
                                              </span>

                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Section : Classes à affecter */}
                                    {devoirInfo.classesManquantes.length > 0 && (
                                      <div className="border border-amber-200 rounded-lg overflow-hidden">
                                        <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                                          <AlertTriangle size={14} className="text-amber-600" />
                                          <span className="font-medium text-amber-800 text-sm">
                                            À affecter ({devoirInfo.classesManquantes.length})
                                          </span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                          {devoirInfo.classesManquantes.map((classe, idx) => (
                                            <div key={idx} className=" xl:text-xs 2xl:text-sm 3xl:text-xl text-base
                                            grid grid-cols-3 items-center gap-3 px-4 py-3 hover:bg-gray-50">
                                              <span className="font-medium text-gray-800">{classe.classeNom}</span>
                                              {classe.profs.length > 0 ? (
                                                <>
                                                  <select
                                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                                                    value={selectedProfs[`${devoirInfo.titre}-${classe.classeNom}`] || ""}
                                                    onChange={(e) => handleProfSelect(devoirInfo.titre, classe.classeNom, e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <option value="" disabled>Sélectionner...</option>
                                                    {classe.profs.map(prof => (
                                                      <option key={prof.id} value={prof.id}>
                                                        {prof.prenom} {prof.nom}
                                                      </option>
                                                    ))}
                                                  </select>
                                                  <div className="text-right">
                                                    <button
                                                      className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg transition-colors ${
                                                        isAffecting ? 'bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
                                                      }`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAffecterProf(devoirInfo, classe);
                                                      }}
                                                      disabled={isAffecting}
                                                    >
                                                      {isAffecting ? '...' : 'Affecter'}
                                                    </button>
                                                  </div>
                                                </>
                                              ) : (
                                                <span className="col-span-2 text-sm text-gray-400 italic">
                                                  Aucun enseignant disponible
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </table>
            </div>

            {/* Version Mobile/Tablette : Cards */}
            <div className="block lg:hidden">
              {filteredEvals.map((item) => {
                if(checkClassOrGroup(item)) {
                  const totalSavoirFaires =
                    item.savoir_faires.nb_vert_plus + item.savoir_faires.nb_vert +
                    item.savoir_faires.nb_vert_pm + item.savoir_faires.nb_yellow +
                    item.savoir_faires.nb_orange + item.savoir_faires.nb_rouge +
                    item.savoir_faires.nb_no_scores;
                  const divisor = totalSavoirFaires || 1;

                  // Infos du devoir commun pour cette évaluation
                  const devoirInfo = commune ? devoirsCommunsInfo[item.titre] : null;
                  const nbManquantes = devoirInfo?.nbManquantes || 0;
                  const isExpanded = expandedEval === item.titre;

                  return (
                    <div key={item.id}
                      className={`mb-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all ${commune ? '' : 'cursor-pointer'} ${isExpanded ? 'border-blue-300' : ''}`}>

                      {/* Partie cliquable */}
                      <div
                        className="p-3 sm:p-4"
                        onClick={() => commune ? setExpandedEval(isExpanded ? null : item.titre) : handleClick(item)}
                      >
                        {/* En-tête de la carte */}
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className={`font-medium text-sm sm:text-base ${commune ? 'text-black' : 'text-blue-600'}`}>
                              {item.titre}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">
                              {formatLettresDate(item.date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Badge affectation pour devoirs communs */}
                            {commune && (
                              nbManquantes > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md animate-pulse">
                                  <AlertTriangle size={12} className="animate-bounce" />
                                  {nbManquantes}
                                  <ChevronDown size={12} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full border border-green-200">
                                  <CheckCircle size={12} />
                                  <ChevronDown size={12} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </span>
                              )
                            )}
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              item.statut === 'terminee'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item.statut === 'terminee' ? <CheckCircle size={12} /> : <Clock size={12} />}
                              <span className="hidden sm:inline">{item.statut}</span>
                            </span>
                          </div>
                        </div>

                        {/* Informations */}
                        <div className="grid grid-cols-2 gap-2 mb-3 text-xs sm:text-sm">
                          {!commune ? (
                            <>
                              <div>
                                <span className="text-gray-500">Absents:</span>
                                <span className="ml-1 font-medium">{item.nombre_eleves}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Moyenne:</span>
                                <span className="ml-1 font-medium">
                                  {item.moyenne_scores ? parseFloat(item.moyenne_scores.toFixed(2)) : "-"}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="col-span-2 flex flex-wrap items-center gap-2">
                              <span>
                                <span className="text-gray-500">Niveau:</span>
                                <span className="ml-1 font-medium">
                                  {(devoirInfo?.niveau || item.classe_nom?.[0]) ? `${devoirInfo?.niveau || item.classe_nom[0]}ème` : '—'}
                                </span>
                              </span>
                              {devoirInfo?.cible && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                                  devoirInfo.cible === 'groupes'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                                }`}>
                                  {devoirInfo.cible === 'groupes' ? 'Affecté aux groupes' : 'Affecté aux classes'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Barre de compétences sur mobile */}
                        {!commune && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Compétences</p>
                            <div className="flex items-center h-3 bg-white rounded border border-gray-200 overflow-hidden">
                              <div className="bg-green-800 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert_plus / divisor})` }}></div>
                              <div className="bg-green-500 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert / divisor})` }}></div>
                              <div className="bg-teal-200 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_vert_pm / divisor})` }}></div>
                              <div className="bg-yellow-500 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_yellow / divisor})` }}></div>
                              <div className="bg-orange-600 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_orange / divisor})` }}></div>
                              <div className="bg-red-600 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_rouge / divisor})` }}></div>
                              <div className="bg-zinc-100 h-full" style={{ width: `calc(100% * ${item.savoir_faires.nb_no_scores / divisor})` }}></div>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          {!commune && (
                            <button className="flex-1 p-2 bg-blue-500 text-white rounded text-xs sm:text-sm hover:bg-blue-600 transition-all">
                              <Eye size={14} className="inline mr-1" />
                              Voir
                            </button>
                          )}
                          {!commune && !/devoir commun/i.test(item.titre) && (
                              <button onClick={(e) => { e.stopPropagation(); setEditEvalId(item.id); }} className="flex-1 p-2 bg-yellow-500 text-white rounded text-xs sm:text-sm hover:bg-yellow-600 transition-all">
                                <Edit size={14} className="inline mr-1" />
                                Modifier
                              </button>
                          )}
                          {(commune || !/devoir commun/i.test(item.titre)) && (
                            <button
                              className="flex-1 p-2 bg-orange-500 text-white rounded text-xs sm:text-sm hover:bg-orange-600 transition-all"
                              onClick={(e) => handleDelete(e, item)}>
                              <Trash2 size={14} className="inline mr-1" />
                              Suppr.
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Section d'expansion pour mobile */}
                      {commune && isExpanded && devoirInfo && (
                        <div className="px-3 pb-3 sm:px-4 sm:pb-4 border-t border-gray-100">
                          <div className="mt-3 bg-white rounded-lg p-3 border border-gray-200">
                            <h4 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                              <User size={14} className="text-emerald-600" />
                              Affectation des responsables de saisie
                            </h4>

                            <div className="space-y-3">
                              {/* Section : Classes affectées */}
                              {devoirInfo.classesAffectees.length > 0 && (
                                <div className="border border-green-200 rounded-lg overflow-hidden">
                                  <div className="bg-green-50 px-3 py-2 border-b border-green-200 flex items-center gap-2">
                                    <CheckCircle size={12} className="text-green-600" />
                                    <span className="font-medium text-green-800 text-xs">
                                      Affectées ({devoirInfo.classesAffectees.length})
                                    </span>
                                  </div>
                                  <div className="divide-y divide-gray-100">
                                    {devoirInfo.classesAffectees.map((classe, idx) => (
                                      <div key={idx} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-xs">
                                        <span className="font-medium text-gray-800">{classe.classeNom}</span>
                                        <span className="text-gray-600 flex items-center gap-1">
                                          <User size={12} className="text-gray-400" />
                                          {classe.profNom}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Section : Classes à affecter */}
                              {devoirInfo.classesManquantes.length > 0 && (
                                <div className="border border-amber-200 rounded-lg overflow-hidden">
                                  <div className="bg-amber-50 px-3 py-2 border-b border-amber-200 flex items-center gap-2">
                                    <AlertTriangle size={12} className="text-amber-600" />
                                    <span className="font-medium text-amber-800 text-xs">
                                      À affecter ({devoirInfo.classesManquantes.length})
                                    </span>
                                  </div>
                                  <div className="divide-y divide-gray-100">
                                    {devoirInfo.classesManquantes.map((classe, idx) => (
                                      <div key={idx} className="px-3 py-2 hover:bg-gray-50">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-medium text-gray-800 text-xs">{classe.classeNom}</span>
                                        </div>
                                        {classe.profs.length > 0 ? (
                                          <div className="flex items-center gap-2">
                                            <select
                                              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                                              value={selectedProfs[`${devoirInfo.titre}-${classe.classeNom}`] || ""}
                                              onChange={(e) => handleProfSelect(devoirInfo.titre, classe.classeNom, e.target.value)}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="" disabled>Sélectionner...</option>
                                              {classe.profs.map(prof => (
                                                <option key={prof.id} value={prof.id}>
                                                  {prof.prenom} {prof.nom}
                                                </option>
                                              ))}
                                            </select>
                                            <button
                                              className={`px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors ${
                                                isAffecting ? 'bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
                                              }`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAffecterProf(devoirInfo, classe);
                                              }}
                                              disabled={isAffecting}
                                            >
                                              {isAffecting ? '...' : 'Affecter'}
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-400 italic">Aucun enseignant disponible</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}

        {selectedEvalId && <EvalDetail selectedEval={selectedEval} setSelectedEvalId={setSelectedEvalId} selectedEvalId={selectedEvalId} />}
      </div>

      {editEvalId && (
        <EditEval
          ideval={editEvalId}
          onClose={() => {
            setEditEvalId(null);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

export default Evaluations;